"use client";

import type React from "react";
import { useCallback, useRef, useState } from "react";
import { NoReferrerConfirmModal } from "@/components/core/NoReferrerConfirmModal";

interface UseNoReferrerGuardReturn {
  /**
   * Gate a purchase on the buyer having a referrer.
   * - `noReferrer === false`: resolves `true` immediately, no modal.
   * - `noReferrer === true`: opens the confirmation modal and resolves
   *   `true` if the buyer proceeds, `false` if they cancel.
   */
  confirmReferrer: (noReferrer: boolean) => Promise<boolean>;
  /**
   * The confirmation modal to render in the consuming component's tree.
   */
  NoReferrerGuardModal: () => React.ReactNode;
}

/**
 * Imperative guard for "purchasing without a referrer". Mirrors
 * {@link useGasPriceGuard}: callers `await confirmReferrer(noReferrer)` inside
 * their purchase handler and only continue when it resolves `true`.
 */
export const useNoReferrerGuard = (): UseNoReferrerGuardReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const resolverRef = useRef<((proceed: boolean) => void) | null>(null);

  const settle = useCallback((proceed: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setIsOpen(false);
    resolve?.(proceed);
  }, []);

  const confirmReferrer = useCallback(
    (noReferrer: boolean): Promise<boolean> => {
      if (!noReferrer) return Promise.resolve(true);
      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setIsOpen(true);
      });
    },
    [],
  );

  const handleProceed = useCallback(() => settle(true), [settle]);
  const handleCancel = useCallback(() => settle(false), [settle]);

  const NoReferrerGuardModal = useCallback(
    () => (
      <NoReferrerConfirmModal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
        onProceed={handleProceed}
        onCancel={handleCancel}
      />
    ),
    [isOpen, settle, handleProceed, handleCancel],
  );

  return { confirmReferrer, NoReferrerGuardModal };
};
